import { useState } from 'react';
import { Dialog, DialogTitle, IconButton, Typography, TextField, Button, Snackbar, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import styles from './gstSubmitDialog.module.css';
import Spinner from 'views/Components/LoadingSinner';
import PropTypes from 'prop-types';

const GstSubmitDialog = ({
    isOpen,
    disableBackdropClick,
    onClose,
    title = 'Submit GST Details',
    dialogWidth,
    customClass = '',
    onSubmit,
    loading,
    isSuccess,
    showCloseButton = true
}) => {
    const [gstNumber, setGstNumber] = useState('');
    const [gstFile, setGstFile] = useState(null);
    const [gstFileBase64, setGstFileBase64] = useState('');
    const [error, setError] = useState('');
    const [gstNumberError, setGstNumberError] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const validExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
    const maxBase64SizeKB = 500;

    const gstRegex = /^([0-9]{2}[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}[0-9]{1}Z{1}[0-9A-Za-z]{1})$/;

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const ext = file.name.split('.').pop().toLowerCase();
        if (!validExtensions.includes(ext)) {
            setError('Invalid file type. Allowed: PDF, JPG, JPEG, PNG');
            setSnackbarOpen(true);
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result;
            const sizeInKB = (base64.length * (3 / 4)) / 1024;

            if (sizeInKB > maxBase64SizeKB) {
                setError(`File size exceeds 500KB. Current size: ${Math.round(sizeInKB)}KB`);
                setSnackbarOpen(true);
                return;
            }

            setGstFile(file);
            setGstFileBase64(base64);
        };
        reader.readAsDataURL(file);
    };

    const validate = () => {
        if (!gstNumber) return 'GST number is required.';
        if (!gstRegex.test(gstNumber)) return 'Invalid GST number format.';
        if (gstNumber.length > 15) return 'GST number must be 15 characters or less.';
        if (!gstFileBase64) return 'GST document is required.';
        return '';
    };
    const validateGstNumber = (value) => {
        const regex = /^([0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9]{1}Z{1}[0-9A-Z]{1})$/;
        if (!value) return 'GST number is required.';
        if (value.length > 15) return 'GST number must be 15 characters or less.';
        if (!regex.test(value)) return 'Invalid GST number format.';
        return '';
    };

    const handleSubmit = () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            setSnackbarOpen(true);
            return;
        }

        const payload = {
            gstNumber,
            documentBase64: gstFileBase64
        };
        if (onSubmit) onSubmit(payload);
        // onClose();
    };

    return (
        <Dialog
            open={isOpen}
            maxWidth={false}
            onClose={disableBackdropClick ? undefined : onClose}
            aria-labelledby="gst-dialog-title"
            classes={{ paper: `${styles.dialogPaper} ${customClass}` }}
            BackdropProps={{
                className: styles.backdrop
            }}
            PaperProps={{
                style: dialogWidth ? { width: dialogWidth } : {}
            }}
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.3 }}
            >
                <DialogTitle disableTypography className={styles.dialogTitle}>
                    <Typography variant="h6" className={styles.titleText}>
                        {title}
                    </Typography>
                    {showCloseButton && (
                        <IconButton aria-label="close" onClick={onClose} className={styles.closeButton} size="small">
                            <CloseIcon />
                        </IconButton>
                    )}
                </DialogTitle>
                <Spinner loading={loading} />

                {isSuccess ? (
                    <motion.div
                        className={styles.successContainer}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                    >
                        <motion.div
                            className={styles.successCircle}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 150, damping: 12 }}
                        >
                            <svg className={styles.checkmark} viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
                                <circle className={styles.checkmarkCircle} cx="26" cy="26" r="25" fill="none" />
                                <path className={styles.checkmarkCheck} fill="none" d="M14 27l7 7 16-16" />
                            </svg>
                        </motion.div>

                        <Typography className={styles.successText}>GST Details Submitted Successfully</Typography>
                    </motion.div>
                ) : (
                    <div className={styles.dialogContent}>
                        <TextField
                            label="GST Number"
                            variant="outlined"
                            fullWidth
                            value={gstNumber}
                            onChange={(e) => {
                                setGstNumber(e.target.value.toUpperCase()); // uppercase for A-Z
                                if (gstNumberError) setGstNumberError(''); // clear while typing
                            }}
                            onBlur={() => {
                                const err = validateGstNumber(gstNumber);
                                setGstNumberError(err);
                            }}
                            className={styles.inputField}
                            inputProps={{ maxLength: 15 }}
                            error={!!gstNumberError}
                            helperText={gstNumberError}
                        />

                        <label className={styles.fileUploadLabel}>
                            Upload GST Document
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} className={styles.fileInput} />
                        </label>

                        {gstFile && (
                            <Typography variant="body2" className={styles.fileName}>
                                Selected: {gstFile.name}
                            </Typography>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            className={styles.submitButton}
                            disabled={!gstNumber || !gstFileBase64}
                        >
                            Submit
                        </Button>
                    </div>
                )}

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={() => setSnackbarOpen(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity="error" onClose={() => setSnackbarOpen(false)}>
                        {error}
                    </Alert>
                </Snackbar>
            </motion.div>
        </Dialog>
    );
};

export default GstSubmitDialog;
GstSubmitDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    disableBackdropClick: PropTypes.bool,
    loading: PropTypes.bool,
    onSubmit: PropTypes.func,
    customClass: PropTypes.string,
    dialogWidth: PropTypes.string,
    showCloseButton: PropTypes.bool,
    isSuccess: PropTypes.bool
};
